import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import { AppSelectController } from "../../components/Core/AppSelect/AppSelect";
import Button from "../../components/Core/Button/Button";
import { CheckboxNewController } from "../../components/Core/Checkbox/CheckboxNew";
import { FormCard } from "../../components/Core/FormCard";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import useUnsavedChangesInFormChecker from "../../hooks/useUnsavedChangesChecker";
import Address from "../../models/Address";
import Consultant, { UpdateConsultantRequest } from "../../models/Consultant";
import { Pool } from "../../models/Organisation";
import { useGeneratePresentation, useUpdateConsultant } from "../../networking/consultant.hooks";
import { useCustomFieldDefinitions, useOrganisation } from "../../networking/organisation.hooks";
import {
	CustomFieldFormValues,
	CustomFieldsSection,
	buildCustomFieldsPayload,
	customFieldFormValues,
	customFieldsSchema,
} from "../../components/Shared/CustomFields";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import {
	useAvailablePools,
	ORGANISATION_DIRECTLY_VALUE,
} from "../../components/Shared/SubsidiarySelect/SubsidiarySelect";
import phoneNumberSchema from "../../schemas/phoneNumberSchema";
import postalCodeSchema from "../../schemas/postalCodeSchema";
import socialSecurityNumberSchema from "../../schemas/socialSecurityNumberSchema";
import { OTHER_DOCUMENT_TYPES } from "../../configs/constants/DocumentTypes";
import TextAreaNew from "../../components/Core/TextArea/TextAreaNew";

type ConsultantsInputs = {
	firstName: string;
	lastName: string;
	email: string;
	profile: {
		address: Address;
		socialSecurityNumber?: string;
		phoneNumber?: string;
		presentation?: string;
		internalNotes?: string;
		confidentialInfo?: string;
		pool?: string;
		subsidiaryId?: string | null;
		title?: string;
		icePhone?: string;
		iceName?: string;
		recruitment?: {
			interviewNotes?: string;
		};
	};
	disablePublishMatchingJobs?: boolean;
	/** Form-level group; mapped into `profile.customFields` on submit. */
	customFields?: CustomFieldFormValues;
};

const ConsultantsInputsValidationSchema = yup.object().shape({
	email: yup.string().email("EMAIL_NOT_VALID").required("FIELD_IS_REQUIRED"),
	firstName: yup.string().required("FIELD_IS_REQUIRED"),
	lastName: yup.string().required("FIELD_IS_REQUIRED"),
	profile: yup.object().shape({
		phoneNumber: phoneNumberSchema.required("FIELD_IS_REQUIRED"),
		socialSecurityNumber: socialSecurityNumberSchema.required("FIELD_IS_REQUIRED"),
		address: yup.object().shape({
			postalCode: postalCodeSchema,
		}),
	}),
});

const ConsultantsProfileTab: React.FC<{ consultant: Consultant }> = ({ consultant }) => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();
	const { mutate: updateConsultant } = useUpdateConsultant();
	//prettier-ignore
	const { mutate: generatePresentation, isLoading: isGeneratingPresentation } = useGeneratePresentation();
	const [aiToolTipText, setAiToolTipText] = React.useState<string>("");

	// Org-defined "Övriga uppgifter" fields (required-ness is form-only).
	const customFieldDefinitions = useCustomFieldDefinitions("consultant");
	const validationSchema = useMemo(
		() =>
			ConsultantsInputsValidationSchema.shape({
				customFields: customFieldsSchema(customFieldDefinitions),
			}),
		[customFieldDefinitions]
	);

	const { register, control, handleSubmit, reset, formState, setValue } =
		useForm<ConsultantsInputs>({
			defaultValues: useMemo(() => {
				return {
					...consultant,
					customFields: customFieldFormValues(
						customFieldDefinitions,
						consultant.profile?.customFields
					),
				};
			}, [consultant, customFieldDefinitions]),
			mode: "onBlur",
			resolver: yupResolver(validationSchema),
		});

	const { subsidiaries } = useSubsidiaries();

	const availablePools = useAvailablePools(
		control,
		"profile.subsidiaryId",
		subsidiaries,
		organisation?.pools,
		consultant.profile?.pool
	);

	// "Inget dotterbolag" uses a sentinel value (Radix forbids an empty
	// SelectItem value); on submit it maps to a null subsidiaryId, clearing any
	// existing link.
	const subsidiaryOptions: { key: string; name: string }[] = [
		{ key: ORGANISATION_DIRECTLY_VALUE, name: t("NO_SUBSIDIARY") },
		...subsidiaries.map((s) => ({ key: s.id, name: s.name })),
	];

	const onSubmit = handleSubmit(async (e) => {
		const updateConsultantProfile: UpdateConsultantRequest = {
			firstName: e.firstName,
			lastName: e.lastName,
			email: e.email,
			profile: {
				address: {
					address1: e.profile.address.address1,
					address2: e.profile.address.address2,
					postalCode: e.profile.address.postalCode,
					city: e.profile.address.city,
				},
				socialSecurityNumber: e.profile.socialSecurityNumber,
				phoneNumber: e.profile.phoneNumber,
				presentation: e.profile.presentation,
				internalNotes: e.profile.internalNotes,
				confidentialInfo: e.profile.confidentialInfo,
				pool: e.profile.pool,
				subsidiaryId:
					e.profile.subsidiaryId && e.profile.subsidiaryId !== ORGANISATION_DIRECTLY_VALUE
						? e.profile.subsidiaryId
						: null,
				iceName: e.profile.iceName,
				icePhone: e.profile.icePhone,
				// Always the full map — the backend replaces it wholesale.
				customFields: buildCustomFieldsPayload(e.customFields, customFieldDefinitions),
			},

			disablePublishMatchingJobs: e.disablePublishMatchingJobs,
		};

		if (consultant?.id) {
			updateConsultant({ userId: consultant.id, update: updateConsultantProfile });
		}
	});

	const onClickAiGenerateButton = () => {
		generatePresentation(
			{
				userId: consultant.id,
			},
			{
				onSuccess: ({ presentation }) => setValue("profile.presentation", presentation),
			}
		);
	};

	useEffect(() => {
		reset({
			...consultant,
			customFields: customFieldFormValues(customFieldDefinitions, consultant.profile?.customFields),
		});
	}, [consultant, reset, customFieldDefinitions]);

	useUnsavedChangesInFormChecker(formState);

	const isDisabledAiGenerateButton = useMemo(() => {
		if (!consultant.profile.competences?.length) {
			setAiToolTipText(t("PUBLIC_PRESENTATION_AI_GENERATE_TOOLTIP_NO_COMPETENCES"));
			return true;
		}

		const otherDocuments = consultant.profile.documents?.otherDocuments;

		if (!otherDocuments || !otherDocuments.length) {
			setAiToolTipText(t("PUBLIC_PRESENTATION_AI_GENERATE_TOOLTIP_NO_CV"));
			return true;
		}

		const cvDocument = otherDocuments.find(({ type }) => type === OTHER_DOCUMENT_TYPES.CV);

		if (!cvDocument) {
			setAiToolTipText(t("PUBLIC_PRESENTATION_AI_GENERATE_TOOLTIP_NO_CV"));
			return true;
		}

		return false;
	}, [consultant.profile.competences, consultant.profile.documents?.otherDocuments]);

	return (
		<div>
			<form onSubmit={onSubmit}>
				<div className="max-w-[1400px] pr-4 py-6">
					<div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
						{/* Left column — identity, address, emergency contact & settings */}
						<div className="flex flex-col gap-5">
							<FormCard title="BASIC_INFO">
								<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
									<AppInputNew
										{...register("firstName")}
										label="FIRST_NAME"
										icon="name"
										placeholder="FIRST_NAME"
										errors={formState.errors?.firstName?.message}
										mandatory
									/>
									<AppInputNew
										{...register("lastName")}
										label="LAST_NAME"
										icon="name"
										placeholder="LAST_NAME"
										errors={formState.errors?.lastName?.message}
										mandatory
									/>
									{/* Full width — addresses are long, and it pairs phone with the
									    personal number on the next row. */}
									<AppInputNew
										{...register("email")}
										containerClassName="md:col-span-2"
										label="EMAIL"
										icon="email"
										placeholder="EMAIL"
										errors={formState.errors?.email?.message}
										mandatory
									/>
									<AppInputNew
										{...register("profile.phoneNumber")}
										label="PHONE"
										icon="phone"
										placeholder="PHONE_PLACEHOLDER"
										errors={formState.errors?.profile?.phoneNumber?.message}
										mandatory
									/>
									<AppInputNew
										{...register("profile.socialSecurityNumber")}
										label="PERSONAL_NUMBER"
										placeholder="ÅÅÅÅMMDD-XXXX"
										errors={formState.errors?.profile?.socialSecurityNumber?.message}
										mandatory
									/>
									{/* Organisation placement, kept paired on its own row at the
									    bottom of the card (leaving the personal number orphaned). */}
									<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:col-span-2 md:grid-cols-2">
										{availablePools.length > 0 && (
											<AppSelectController<Pool>
												control={control}
												label="POOL"
												name="profile.pool"
												options={availablePools}
												getOptionLabel={(option) => option.name}
												getOptionValue={(o) => o.key as string}
												errors={formState.errors?.profile?.pool?.message}
												placeholder="SELECT_POOL"
												margin="0"
												mandatory
											/>
										)}
										{subsidiaries.length > 0 && (
											<AppSelectController<{ key: string; name: string }>
												control={control}
												label="BELONGS_TO_SUBSIDIARY"
												name="profile.subsidiaryId"
												options={subsidiaryOptions}
												getOptionLabel={(o) => o.name}
												getOptionValue={(o) => o.key}
												placeholder="NO_SUBSIDIARY"
												margin="0"
											/>
										)}
									</div>
								</div>
								{/* Consultant fields placed under Grunduppgifter — hidden when there are none. */}
								<CustomFieldsSection
									entity="consultant"
									placement="grunduppgifter"
									variant="inline"
									definitions={customFieldDefinitions}
									control={control}
									register={register}
									errors={formState.errors}
								/>
							</FormCard>

							<FormCard title="ADDRESS">
								<div className="flex flex-col gap-4">
									<AppInputNew
										{...register("profile.address.address1")}
										label="STREET_ADDRESS"
										placeholder="ADDRESS_PLACEHOLDER"
									/>
									<AppInputNew
										{...register("profile.address.address2")}
										placeholder="ADDRESS_PLACEHOLDER_2"
									/>
									<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
										<AppInputNew
											{...register("profile.address.postalCode")}
											label="POSTAL_CODE"
											placeholder="POSTAL_CODE_PLACEHOLDER"
											errors={formState.errors?.profile?.address?.postalCode?.message}
										/>
										<AppInputNew
											{...register("profile.address.city")}
											label="CITY"
											placeholder="CITY_PLACEHOLDER"
										/>
									</div>
								</div>
							</FormCard>

							<FormCard title="EMERGENCY_CONTACT">
								<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
									<AppInputNew
										{...register("profile.iceName")}
										label="ice.iceName"
										placeholder="ice.iceNamePlaceholder"
									/>
									<AppInputNew
										{...register("profile.icePhone")}
										label="ice.icePhone"
										placeholder="ice.icePhonePlaceholder"
									/>
								</div>
							</FormCard>

							<FormCard title="SETTINGS">
								<CheckboxNewController
									control={control}
									field="disablePublishMatchingJobs"
									label="consultant.doNotPublish"
									hint="consultant.doNotPublishDesc"
								/>
								{/* Consultant fields placed under Anställning — hidden when there are none. */}
								<CustomFieldsSection
									entity="consultant"
									placement="anstallning"
									variant="inline"
									definitions={customFieldDefinitions}
									control={control}
									register={register}
									errors={formState.errors}
								/>
							</FormCard>
						</div>

						{/* Right column — public presentation & internal notes */}
						<div className="flex flex-col gap-5">
							<FormCard title="PUBLIC_PRESENTATION">
								<TextAreaNew
									{...register("profile.presentation")}
									rows={7}
									autoGrowOnFocus
									maxRows={20}
									placeholder="PUBLIC_PRESENTATION"
									aiGenerateButton={{
										onClickAiGenerateButton,
										aiGenerateButtonText: "PUBLIC_PRESENTATION_AI_GENERATE",
										disabledAiGenerateButton: isDisabledAiGenerateButton,
										loadingAiGenerateButton: isGeneratingPresentation,
										tooltipText: aiToolTipText,
										loadingText: "Generating...",
									}}
								/>
							</FormCard>

							<FormCard title="NOTES">
								<div className="flex flex-col gap-4">
									<TextAreaNew
										{...register("profile.internalNotes")}
										label="PRIVATE_NOTES"
										placeholder="PRIVATE_NOTES"
										rows={10}
										autoGrowOnFocus
										maxRows={20}
									/>
									<TextAreaNew
										{...register("profile.confidentialInfo")}
										label="EVENTS_NOTE"
										placeholder="EVENTS_NOTE"
										height="165px"
									/>
									<TextAreaNew
										{...register("profile.recruitment.interviewNotes")}
										height="165px"
										label="INTERVIEW_NOTES"
										placeholder="INTERVIEW_NOTES_PLACEHOLDER"
										disabled={true}
									/>
								</div>
							</FormCard>

							{/* Consultant fields placed under "Övriga uppgifter" — hidden when there are none. */}
							<CustomFieldsSection
								entity="consultant"
								placement="ovrigt"
								definitions={customFieldDefinitions}
								control={control}
								register={register}
								errors={formState.errors}
							/>
						</div>
					</div>
				</div>
				<FloatingButtonPanel>
					<Button width="9.75rem">{t("SAVE_CHANGES")}</Button>
				</FloatingButtonPanel>
			</form>
		</div>
	);
};

export default ConsultantsProfileTab;
